import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import BankReconciliationApi from "../../../../../infrastructure/api/bankReconciliation";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const formatMoney = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("fa-IR");

const pick = (row, camel, pascal) => row?.[camel] ?? row?.[pascal];

const STATUS = {
  Pending: 1,
  Classified: 2,
  Ignored: 3,
};

const TYPES_REQUIRING_REFERENCE = new Set([1, 2, 4]); // وام، آورده، پیمانکار

const CARD_SX = {
  height: "100%",
  borderRadius: "16px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  border: "1px solid",
  borderColor: "divider",
};

const TABLE_HEAD_SX = {
  bgcolor: "#F8FAFC",
  "& .MuiTableCell-head": {
    fontWeight: 700,
    whiteSpace: "nowrap",
    color: "text.secondary",
    fontSize: 13,
  },
};

const normalizeLine = (raw) => ({
  id: pick(raw, "id", "Id"),
  rowNo: pick(raw, "rowNo", "RowNo"),
  transactionDateJalali: pick(raw, "transactionDateJalali", "TransactionDateJalali") || "",
  transactionTime: pick(raw, "transactionTime", "TransactionTime") || "",
  creditAmount: Number(pick(raw, "creditAmount", "CreditAmount") || 0),
  debitAmount: Number(pick(raw, "debitAmount", "DebitAmount") || 0),
  amount: Number(pick(raw, "amount", "Amount") || 0),
  isCredit: Boolean(pick(raw, "isCredit", "IsCredit")),
  balanceAfter: Number(pick(raw, "balanceAfter", "BalanceAfter") || 0),
  description: pick(raw, "description", "Description") || "",
  counterparty: pick(raw, "counterparty", "Counterparty") || "",
  serialNumber: pick(raw, "serialNumber", "SerialNumber") || "",
  status: Number(pick(raw, "status", "Status") || 0),
  statusTitle: pick(raw, "statusTitle", "StatusTitle") || "",
  suggestedType: Number(pick(raw, "suggestedType", "SuggestedType") || 0),
  suggestedTypeTitle: pick(raw, "suggestedTypeTitle", "SuggestedTypeTitle") || "",
  suggestedRequestInfoId: pick(raw, "suggestedRequestInfoId", "SuggestedRequestInfoId"),
  suggestedRequestReference:
    pick(raw, "suggestedRequestReference", "SuggestedRequestReference") || "",
  suggestionReason: pick(raw, "suggestionReason", "SuggestionReason") || "",
  classifiedType: pick(raw, "classifiedType", "ClassifiedType"),
  classifiedTypeTitle: pick(raw, "classifiedTypeTitle", "ClassifiedTypeTitle") || "",
  requestInfoId: pick(raw, "requestInfoId", "RequestInfoId"),
  requestReference: pick(raw, "requestReference", "RequestReference") || "",
  classificationNote: pick(raw, "classificationNote", "ClassificationNote") || "",
});

const isConfirmedStatus = (status) =>
  Number(status) === STATUS.Classified || Number(status) === STATUS.Ignored;

const SummaryCard = ({ label, value, hint, accent }) => (
  <Card sx={{ ...CARD_SX, borderTop: accent ? `3px solid` : undefined, borderTopColor: accent }}>
    <CardContent sx={{ py: 1.75, px: 2, "&:last-child": { pb: 1.75 } }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
        {label}
      </Typography>
      <Typography
        fontWeight={700}
        sx={{ mt: 0.75, fontSize: { xs: 16, md: 18 }, lineHeight: 1.35, letterSpacing: 0 }}
        dir="ltr"
      >
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const EmptyState = ({ loading, message }) => (
  <Box sx={{ py: 6, textAlign: "center" }}>
    <Typography variant="body2" color="text.secondary">
      {loading ? "در حال بارگذاری..." : message}
    </Typography>
  </Box>
);

const BankReconciliationPage = ({ source = "Management" }) => {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [classified, setClassified] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [reviewLine, setReviewLine] = useState(null);
  const [formType, setFormType] = useState(0);
  const [formReferenceId, setFormReferenceId] = useState("");
  const [formNote, setFormNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const [search, setSearch] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryRes, pendingRes, classifiedRes, typesRes] = await Promise.all([
        BankReconciliationApi.getAccountSummary(source),
        BankReconciliationApi.getPendingLines(source),
        BankReconciliationApi.getClassifiedLines(source, null, 500),
        BankReconciliationApi.getTransactionTypes(source),
      ]);
      if (!summaryRes.ok) {
        setError(summaryRes.message || "خطا در دریافت موجودی حساب");
      } else {
        setSummary(summaryRes.data);
      }
      if (pendingRes.ok) {
        setPending((pendingRes.data || []).map(normalizeLine));
      }
      if (classifiedRes.ok) {
        setClassified((classifiedRes.data || []).map(normalizeLine));
      }
      if (typesRes.ok) {
        setTypes(
          (typesRes.data || []).map((t) => ({
            value: Number(t.value ?? t.Value),
            title: t.title ?? t.Title ?? "",
          }))
        );
      }
    } catch {
      setError("خطا در بارگذاری صفحه مدیریت حساب");
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openReview = (line, mode = "pending") => {
    setReviewLine({ ...line, _editMode: mode });
    if (mode === "edit" || isConfirmedStatus(line.status)) {
      setFormType(Number(line.classifiedType) || Number(line.suggestedType) || 0);
      setFormReferenceId(
        line.requestReference
          ? String(line.requestReference)
          : line.suggestedRequestReference
            ? String(line.suggestedRequestReference)
            : ""
      );
      setFormNote(line.classificationNote || line.suggestionReason || "");
      return;
    }
    setFormType(Number(line.suggestedType) || 0);
    setFormReferenceId(
      line.suggestedRequestReference ? String(line.suggestedRequestReference) : ""
    );
    setFormNote(line.suggestionReason || "");
  };

  const closeReview = () => {
    setReviewLine(null);
    setFormType(0);
    setFormReferenceId("");
    setFormNote("");
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const res = await BankReconciliationApi.importMellatCsv(source, file);
      if (!res.ok) {
        setError(res.message || "ایمپورت ناموفق بود");
        toast.error(res.message || "ایمپورت ناموفق بود");
        return;
      }
      toast.success(res.message || "ایمپورت انجام شد");
      await loadAll();
    } catch {
      setError("خطا در آپلود فایل");
      toast.error("خطا در آپلود فایل");
    } finally {
      setUploading(false);
    }
  };

  const needsReferenceId = TYPES_REQUIRING_REFERENCE.has(Number(formType));
  const isEditingConfirmed = Boolean(
    reviewLine && (reviewLine._editMode === "edit" || isConfirmedStatus(reviewLine.status))
  );

  const filteredClassified = useMemo(() => {
    const q = String(search || "").trim();
    if (!q) return classified;
    return classified.filter((line) => {
      const hay = [
        line.serialNumber,
        line.description,
        line.counterparty,
        line.requestReference,
        line.classifiedTypeTitle,
        line.classificationNote,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [classified, search]);

  const handleRevertToPending = async (line, { closeDialog = false } = {}) => {
    if (!line?.id) return;
    const ok = window.confirm(
      `ردیف سریال «${line.serialNumber || line.id}» از درخواست ${
        line.requestReference || "—"
      } جدا شود و به لیست در انتظار (Pending) برگردد؟`
    );
    if (!ok) return;

    setRevertingId(line.id);
    try {
      const res = await BankReconciliationApi.revertLineToPending(source, {
        lineId: line.id,
        note: "برگشت برای انتساب به متقاضی دیگر",
      });
      if (!res.ok) {
        toast.error(res.message || "برگشت به Pending ناموفق بود");
        return;
      }
      toast.success(res.message || "به لیست Pending برگشت");
      if (closeDialog) closeReview();
      setTab(0);
      await loadAll();
    } catch {
      toast.error("خطا در برگشت ردیف به Pending");
    } finally {
      setRevertingId(null);
    }
  };

  const handleSaveClassification = async () => {
    if (!reviewLine) return;
    if (!formType || Number(formType) === 0) {
      toast.error("نوع تراکنش را انتخاب کنید");
      return;
    }
    if (needsReferenceId && !String(formReferenceId || "").trim()) {
      toast.error("شناسه مرجع درخواست (ReferenceId) الزامی است");
      return;
    }
    setSaving(true);
    try {
      const res = await BankReconciliationApi.classifyLine(source, {
        lineId: reviewLine.id,
        transactionType: Number(formType),
        requestReferenceId: needsReferenceId
          ? String(formReferenceId).trim()
          : null,
        note: formNote || "",
      });
      if (!res.ok) {
        toast.error(res.message || "ذخیره ناموفق بود");
        return;
      }
      toast.success(
        isEditingConfirmed
          ? res.message || "ویرایش ذخیره شد"
          : res.message || "ذخیره شد"
      );
      closeReview();
      await loadAll();
    } catch {
      toast.error("خطا در ذخیره تعیین تکلیف");
    } finally {
      setSaving(false);
    }
  };

  const balanceDiff = useMemo(() => {
    if (!summary) return 0;
    return Number(summary.balanceDifference ?? summary.BalanceDifference ?? 0);
  }, [summary]);

  const balanceOk = Math.abs(balanceDiff) < 1;
  const bankAccount = summary?.bankAccountNumber ?? summary?.BankAccountNumber ?? "—";
  const bankName = summary?.bankName ?? summary?.BankName ?? "";

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 1.5 }, background: "#F6F8FB", minHeight: "100%" }}>
      <BreadcrumbsComponent />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1.5}
        sx={{ mt: 1, mb: 2 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountBalanceIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>
              مدیریت حساب بانک ملت
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            ایمپورت صورتحساب، تطبیق موجودی، و تعیین تکلیف تراکنش‌های وام و آورده
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title="بازخوانی">
            <span>
              <IconButton color="primary" onClick={loadAll} disabled={loading || uploading}>
                <SyncIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={uploading || loading}
          >
            {uploading ? "در حال آپلود..." : "آپلود CSV ملت"}
            <input hidden type="file" accept=".csv,text/csv" onChange={handleUpload} />
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="شماره حساب"
            value={bankAccount}
            hint={bankName || "بانک ملت"}
            accent="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="موجودی پلتفرم"
            value={formatMoney(summary?.bookBalance ?? summary?.BookBalance)}
            hint="ریال"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="مانده صورتحساب بانک"
            value={formatMoney(summary?.lastStatementBalance ?? summary?.LastStatementBalance)}
            hint="آخرین CSV"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="اختلاف موجودی"
            value={formatMoney(balanceDiff)}
            hint={balanceOk ? "تطبیق‌شده" : "نیاز به بررسی"}
            accent={balanceOk ? "success.main" : "warning.main"}
          />
        </Grid>
      </Grid>

      <Card sx={{ ...CARD_SX, overflow: "hidden" }}>
        <CardHeader
          sx={{ py: 1.25, px: 2, "& .MuiCardHeader-action": { m: 0, alignSelf: "center" } }}
          title={
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 40,
                "& .MuiTab-root": { minHeight: 40, py: 1, fontWeight: 600 },
              }}
            >
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>در انتظار</span>
                    <Chip size="small" color="info" label={pending.length} sx={{ height: 22 }} />
                  </Stack>
                }
              />
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>تأییدشده</span>
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={classified.length}
                      sx={{ height: 22 }}
                    />
                  </Stack>
                }
              />
            </Tabs>
          }
          action={
            tab === 1 ? (
              <TextField
                size="small"
                placeholder="جستجو سریال، شرح، ReferenceId"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: { xs: 180, sm: 260 }, mr: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            ) : null
          }
        />
        <Divider />

        {tab === 0 ? (
          pending.length === 0 ? (
            <EmptyState loading={loading} message="ردیفی در صف بررسی نیست." />
          ) : (
            <TableContainer sx={{ maxHeight: { md: "62vh" } }}>
              <Table size="small" stickyHeader>
                <TableHead sx={TABLE_HEAD_SX}>
                  <TableRow>
                    <TableCell>تاریخ</TableCell>
                    <TableCell>شرح</TableCell>
                    <TableCell>طرف مقابل</TableCell>
                    <TableCell align="left">مبلغ</TableCell>
                    <TableCell>پیشنهاد سیستم</TableCell>
                    <TableCell align="center" width={96}>
                      عملیات
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pending.map((line) => (
                    <TableRow key={line.id} hover sx={{ "& td": { py: 1.25 } }}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" fontWeight={600}>
                          {line.transactionDateJalali}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {line.transactionTime}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" noWrap title={line.description}>
                          {line.description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography variant="body2" noWrap title={line.counterparty}>
                          {line.counterparty || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Chip
                          size="small"
                          color={line.isCredit ? "success" : "error"}
                          variant="outlined"
                          label={`${line.isCredit ? "+" : "−"} ${formatMoney(line.amount)}`}
                          sx={{ fontWeight: 600, direction: "ltr" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{line.suggestedTypeTitle || "—"}</Typography>
                        {line.suggestedRequestReference ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            درخواست {line.suggestedRequestReference}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="contained" onClick={() => openReview(line, "pending")}>
                          بررسی
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : filteredClassified.length === 0 ? (
          <EmptyState loading={loading} message="ردیفی یافت نشد." />
        ) : (
          <TableContainer sx={{ maxHeight: { md: "62vh" } }}>
            <Table size="small" stickyHeader>
              <TableHead sx={TABLE_HEAD_SX}>
                <TableRow>
                  <TableCell>تاریخ / سریال</TableCell>
                  <TableCell>شرح</TableCell>
                  <TableCell align="left">مبلغ</TableCell>
                  <TableCell>نوع</TableCell>
                  <TableCell>درخواست</TableCell>
                  <TableCell>وضعیت</TableCell>
                  <TableCell align="center" width={120}>
                    عملیات
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClassified.map((line) => (
                  <TableRow key={`c-${line.id}`} hover sx={{ "& td": { py: 1.25 } }}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2" fontWeight={600}>
                        {line.transactionDateJalali}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" dir="ltr">
                        {line.serialNumber || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap title={line.description}>
                        {line.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Chip
                        size="small"
                        color={line.isCredit ? "success" : "error"}
                        variant="outlined"
                        label={`${line.isCredit ? "+" : "−"} ${formatMoney(line.amount)}`}
                        sx={{ fontWeight: 600, direction: "ltr" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{line.classifiedTypeTitle || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} dir="ltr">
                        {line.requestReference || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={line.statusTitle || "—"} variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="ویرایش">
                          <IconButton size="small" color="primary" onClick={() => openReview(line, "edit")}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="برگشت به Pending">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={revertingId === line.id}
                              onClick={() => handleRevertToPending(line)}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={Boolean(reviewLine)} onClose={closeReview} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEditingConfirmed ? "ویرایش تعیین تکلیف" : "تعیین تکلیف تراکنش"}
        </DialogTitle>
        <DialogContent>
          {reviewLine ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity={isEditingConfirmed ? "warning" : "info"} sx={{ borderRadius: 2 }}>
                {isEditingConfirmed
                  ? "با ذخیره، اتصال قبلی لغو و تعیین تکلیف جدید اعمال می‌شود."
                  : "پیش‌فرض از روی شرح/مبلغ پیشنهاد شده است. در صورت نیاز تغییر دهید و ذخیره کنید."}
                {reviewLine.suggestionReason && !isEditingConfirmed
                  ? ` — ${reviewLine.suggestionReason}`
                  : ""}
              </Alert>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#F8FAFC",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Grid container spacing={1.25}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      تاریخ
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {reviewLine.transactionDateJalali} {reviewLine.transactionTime}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      مبلغ
                    </Typography>
                    <Typography variant="body2" fontWeight={600} dir="ltr">
                      {formatMoney(reviewLine.amount)} ({reviewLine.isCredit ? "واریز" : "برداشت"})
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      شرح
                    </Typography>
                    <Typography variant="body2">{reviewLine.description}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      طرف مقابل
                    </Typography>
                    <Typography variant="body2">{reviewLine.counterparty || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      سریال
                    </Typography>
                    <Typography variant="body2" dir="ltr">
                      {reviewLine.serialNumber || "—"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>نوع تراکنش</InputLabel>
                <Select
                  label="نوع تراکنش"
                  value={formType || ""}
                  onChange={(e) => setFormType(Number(e.target.value))}
                >
                  {types.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {needsReferenceId ? (
                <TextField
                  fullWidth
                  size="small"
                  label="شناسه مرجع درخواست (ReferenceId)"
                  value={formReferenceId}
                  onChange={(e) => setFormReferenceId(e.target.value)}
                  helperText={
                    isEditingConfirmed && reviewLine.requestReference
                      ? `فعلی: ${reviewLine.requestReference}`
                      : reviewLine.suggestedRequestReference
                        ? `پیشنهاد سیستم: ${reviewLine.suggestedRequestReference}`
                        : "برای وام / آورده / پیمانکار الزامی است"
                  }
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  برای این نوع تراکنش شماره پیگیری درخواست لازم نیست.
                </Typography>
              )}
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="یادداشت"
                value={formNote}
                onChange={(e) => setFormNote(e.target.value)}
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
          {isEditingConfirmed ? (
            <Button
              color="warning"
              variant="outlined"
              startIcon={<ReplayIcon />}
              disabled={saving || revertingId === reviewLine?.id}
              onClick={() => handleRevertToPending(reviewLine, { closeDialog: true })}
            >
              {revertingId === reviewLine?.id ? "..." : "برگشت به Pending"}
            </Button>
          ) : (
            <span />
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={closeReview}>انصراف</Button>
            <Button variant="contained" disabled={saving} onClick={handleSaveClassification}>
              {saving ? "در حال ذخیره..." : isEditingConfirmed ? "ذخیره ویرایش" : "ذخیره"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankReconciliationPage;
